import React, { useState } from 'react';
import { PenTool, Loader } from 'lucide-react';
import { aiAPI } from '../services/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import StatsAndTabs from './StatsAndTabs';

const LegalBrief = ({ onSuccess, stats, loading: statsLoading }) => {
  const [formData, setFormData] = useState({
    topic: '',
    keyPoints: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.topic.trim() || !formData.keyPoints.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      const response = await aiAPI.generateBrief({
        topic: formData.topic,
        keyPoints: formData.keyPoints
      });

      setResult(response.result);
      toast.success('Legal brief generated!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Legal brief generation failed:', error);
      toast.error('Failed to generate legal brief');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      topic: '',
      keyPoints: ''
    });
    setResult('');
  };

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Legal Brief</h2>
          <p>Generate professional legal briefs with AI assistance</p>
        </div>
      </div>

      <StatsAndTabs stats={stats} loading={statsLoading} />

      <div className="tool-content">
        <div className="tool-header">
          <PenTool size={24} />
          <h3>Legal Brief Generator</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Brief Topic</label>
            <input
              type="text"
              name="topic"
              className="form-input"
              placeholder="Enter the main topic or issue for the brief..."
              value={formData.topic}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Key Points</label>
            <textarea
              name="keyPoints"
              className="form-textarea"
              placeholder="Enter key points, arguments, and relevant details to include..."
              value={formData.keyPoints}
              onChange={handleChange}
              disabled={loading}
              style={{ minHeight: '150px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Generating Brief...
                </>
              ) : (
                <>
                  <PenTool size={16} />
                  Generate Brief
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {result && (
        <div className="tool-content">
          <div className="tool-header">
            <PenTool size={24} />
            <h3>Generated Legal Brief</h3>
          </div>
          
          <MarkdownRenderer content={result} />

          <div style={{ marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigator.clipboard.writeText(result)}
            >
              Copy Brief
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalBrief;
